/**
 * Which deployment of this app is running.
 *
 * One codebase serves two Vercel projects: Firefleet (Firefly Drone Shows) and
 * SwarmSystem (Swarm Defense). They are the same application with different
 * branding and different backends.
 *
 * Naming note: this is deliberately NOT called "brand". Three unrelated things
 * in this codebase already use that word — the PDF letterhead picker in
 * `Admin/ManufacturingReport.tsx`, the serial-prefix `Brand` in
 * `Procurement/PurchaseOrderForm.tsx`, and the fleet-visibility toggle in
 * `Settings/SettingsPage.js`. "Instance" means the deployment, and only that.
 *
 * Set `REACT_APP_INSTANCE` per Vercel project. It is a Create React App
 * environment variable, so it is baked in at BUILD time — each project builds
 * its own bundle. Anything unset or unrecognized falls back to `firefly`, which
 * is exactly today's behavior.
 *
 * The accent color is NOT read from here by most of the UI. ~1,500 usages
 * across ~197 files go through the Tailwind `primary` token, which switches in
 * `tailwind.config.js` off the same variable. This export is only for the
 * handful of places that hardcode a hex in an inline style.
 */

import fireflyLogo from "./img/fflogo.png";
// The near-WHITE lockup. The nav sits on #191919, so it needs the variant drawn
// for dark backgrounds. `swarm-logo-secondary-light.webp` is the same mark in
// near-black for light backgrounds — correct for the PDF letterhead in
// Admin/ManufacturingReport.tsx, invisible in the nav. Easy to mix up.
import swarmLogo from "./img/swarm-logo-primary-dark.webp";
// The spinner mark. Firefly's is a bundled import because that is where the file
// already lives; Swarm's comes from `public/` because the shield is already there
// as the app icon and a second copy in `src/img/` would be the same bytes twice.
import fireflyLoadingIcon from "./img/loading.png";

export type Instance = "firefly" | "swarm";

export const INSTANCE: Instance =
	process.env.REACT_APP_INSTANCE === "swarm" ? "swarm" : "firefly";

export const isSwarm = INSTANCE === "swarm";

/**
 * Running off the dev server (`./dev.sh`, `./swarm-dev.sh`), as opposed to a
 * production build. The tab title and the PWA name carry a `[dev]` prefix in
 * that case, so a dev tab beside a prod tab, or a dev PWA beside the real one
 * on a phone, is told apart at a glance. `src/setupProxy.js` does the manifest
 * half, because the manifest is a static file the browser reads on its own.
 */
export const isDev = process.env.NODE_ENV === "development";

/** The name the app calls itself in the tab: the instance's, `[dev]`-prefixed on the dev server. */
export function appTitle(): string {
	return isDev ? `[dev] ${instance.displayName}` : instance.displayName;
}

type InstanceConfig = {
	/** Shown in the browser tab, the PWA name, and anywhere the app names itself. */
	displayName: string;
	/** The organization the deployment belongs to. */
	orgName: string;
	/** Matches Tailwind's `primary` token. For inline styles that cannot use it. */
	accent: string;
	/**
	 * Rallying cry in the dashboard marquee. `null` drops the segment rather than
	 * printing something generic — better a shorter marquee than a hollow slogan.
	 */
	tagline: string | null;
	/** Horizontal lockup for the nav bar. */
	logo: string;
	logoAlt: string;
	/**
	 * Square mark for the spinner (`LoadingIcon`). This is the APP ICON, not the
	 * lockup — it renders at 35px and rotates, so the horizontal wordmark is
	 * unusable here. Firefly's `img/loading.png` is byte-identical to
	 * `public/logo192.png`, so both instances are "the 192 app icon" and the
	 * relationship holds on each side.
	 */
	loadingIcon: string;
	favicon: string;
	manifest: string;
	appleTouchIcon: string;
	/**
	 * The same two icons with a DEV ribbon, generated by
	 * `scripts/make-dev-icons.py` from the real ones. Used only on the dev server.
	 */
	devFavicon: string;
	devAppleTouchIcon: string;
};

const url = (p: string) => `${process.env.PUBLIC_URL || ""}${p}`;

const CONFIG: Record<Instance, InstanceConfig> = {
	firefly: {
		displayName: "Firefleet",
		orgName: "Firefly Drone Shows",
		accent: "#ffcc33",
		tagline: "DO DRONE SHOWS",
		logo: fireflyLogo,
		logoAlt: "Firefleet logo",
		loadingIcon: fireflyLoadingIcon,
		favicon: url("/favicon.ico"),
		manifest: url("/manifest.json"),
		appleTouchIcon: url("/logo192.png"),
		devFavicon: url("/dev-favicon.png"),
		devAppleTouchIcon: url("/dev-logo192.png"),
	},
	swarm: {
		// MUST match REACT_APP_INSTANCE_NAME on the SwarmSystems Vercel project: the
		// env var paints the served html, this paints everything after boot, so a
		// mismatch shows as the title visibly changing once JavaScript runs.
		//
		// Matches the swarmdefense.systems domain. The ".Systems" is load-bearing:
		// "Swarm Defense" is the company, and the suffix is what marks this as the
		// tool — the same job "Firefleet" does for Firefly. Firefleet needs no
		// suffix because its name already differs from the company's.
		displayName: "SwarmDefense.Systems",
		orgName: "Swarm Defense",
		// Muted blue from the Special palette in swarm-branding.pdf. The brand
		// "primary" (#0D1D23 slate) and the secondary gray ramp are background and
		// surface colors; neither can serve as an accent on a dark UI.
		accent: "#82b7ce",
		// `null` is supported here and drops the marquee segment entirely, if this
		// ever needs to go quiet.
		tagline: "DO DRONE STRIKES",
		logo: swarmLogo,
		logoAlt: "SwarmSystem logo",
		// The shield, on its own. `swarm-logo-primary-dark.webp` is the wide
		// lockup and would render as an illegible smear at 35px.
		loadingIcon: url("/swarm-logo192.png"),
		favicon: url("/swarm-favicon.ico"),
		manifest: url("/manifest-swarm.json"),
		appleTouchIcon: url("/swarm-logo192.png"),
		devFavicon: url("/dev-swarm-favicon.png"),
		devAppleTouchIcon: url("/dev-swarm-logo192.png"),
	},
};

export const instance = CONFIG[INSTANCE];

function setLinkHref(rel: string, href: string): void {
	let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
	if (!el) {
		el = document.createElement("link");
		el.rel = rel;
		document.head.appendChild(el);
	}
	el.href = href;
}

/**
 * Point the document chrome at this instance: tab title, favicon, PWA manifest,
 * and the `--color-primary` custom property.
 *
 * Applied unconditionally rather than only for Swarm. For Firefly every value
 * already matches what `public/index.html` ships, so it is a no-op — but that
 * keeps one code path instead of two, and guarantees `--color-primary` is
 * always defined (today it is referenced in `Vehicles/VehiclesPage.tsx` and
 * never set, so it silently rides its fallback).
 *
 * Called once from `index.js` before render.
 */
export function applyInstanceChrome(): void {
	document.title = appTitle();
	// On the dev server the icons carry a DEV ribbon too, so a dev tab and a dev
	// home-screen install are told apart by the picture, not only by the name.
	setLinkHref("icon", isDev ? instance.devFavicon : instance.favicon);
	setLinkHref("manifest", instance.manifest);
	setLinkHref("apple-touch-icon", isDev ? instance.devAppleTouchIcon : instance.appleTouchIcon);
	document.documentElement.style.setProperty(
		"--color-primary",
		instance.accent,
	);
}
